// Editor body for the dev impersonation entry.
// State + persistence live in useDevImpersonationEditor; role labels in devImpersonationRoles.
// The `containerOpen` prop tells the editor when its parent (drawer / popover) just
// became visible so it can rehydrate from storage and load org lists.
//
// Mobile viewport is rendered as its own impersonation axis (dev sync note
// c238ce65). Selecting a preset stages it; Apply commits roles + viewport
// together. Apply enables when EITHER axis is dirty.
import type { AuthContextValue } from "@/contexts/AuthContext";
import { DEV_IMPERSONATION_ROLE_CHOICES } from "./devImpersonationRoles";
import { isUuidLike } from "@/lib/devImpersonationStorage";
import { DevImpersonationViewportAxis } from "./DevImpersonationViewportAxis";
import {
  useDevImpersonationEditor,
  type DevImpersonationTrustOverride,
} from "./useDevImpersonationEditor";
import { FIREFLY_TARGET_ATTR } from "@/lib/firefly/fireflyTargetConvention";

type Props = {
  /** Was named `drawerOpen` while this lived under SettingsDrawer; renamed for popover use. */
  containerOpen: boolean;
  auth: AuthContextValue;
};

export function DevImpersonationSettingsSection({ containerOpen, auth }: Props) {
  const editor = useDevImpersonationEditor({ containerOpen, auth });
  const {
    canUse,
    realPlatformDev,
    enabled,
    setEnabled,
    selected,
    toggleRole,
    browseAllOrgs,
    setBrowseAllOrgs,
    trustLevelOverride,
    setTrustLevelOverride,
    platformOrgs,
    orgOptions,
    workspaceOrganizationId,
    setWorkspaceOrganizationId,
    msg,
    persist,
    applyAll,
    canApply,
    clearAll,
    pendingViewport,
    setPendingViewport,
    viewportActive,
    viewportDesktopBrowser,
  } = editor;

  if (!canUse) return null;

  return (
    <section className="space-y-3 rounded-2xl border border-amber-300/80 dark:border-amber-800/60 bg-amber-50/90 dark:bg-amber-950/30 px-3 py-3">
      <h3 className="text-xs font-semibold uppercase tracking-widest text-amber-900 dark:text-amber-200">
        Impersonation axis
      </h3>
      <p className="text-[11px] text-amber-900/85 dark:text-amber-200/85 leading-snug">
        Simulates <strong>permission checks in this browser only</strong>. Supabase and API routes still enforce your
        real JWT — expect mismatches for RLS-blocked reads/writes when you drop platform roles.
      </p>

      {auth.impersonation.active ? (
        <div className="text-[11px] font-medium text-amber-800 dark:text-amber-300">
          Active: {auth.impersonation.roles.join(", ") || "—"}
        </div>
      ) : null}
      {msg ? <div className="text-[11px] text-amber-900 dark:text-amber-200">{msg}</div> : null}

      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-amber-950 dark:text-amber-100">Impersonation mode</span>
        <button
          type="button"
          onClick={() => {
            const next = !enabled;
            setEnabled(next);
            if (!next) persist(false, new Set(), false);
          }}
          className={
            "relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors " +
            (enabled ? "bg-amber-600" : "bg-gray-300 dark:bg-gray-600")
          }
          role="switch"
          aria-checked={enabled}
        >
          <span
            className={
              "inline-block h-4 w-4 rounded-full bg-white shadow transform transition-transform " +
              (enabled ? "translate-x-6" : "translate-x-1")
            }
          />
        </button>
      </div>

      <div className={enabled ? "" : "opacity-45 pointer-events-none"}>
        <p className="text-[11px] text-amber-900/80 dark:text-amber-200/80 mb-1.5">Simulated roles (multi-select)</p>
        <p className="text-[10px] text-amber-800/85 dark:text-amber-300/80 mb-1.5 leading-snug">
          Legacy DB strings <span className="font-mono">org_owner</span> / <span className="font-mono">org_admin</span> match{" "}
          <strong>Org owner</strong> / <strong>Org admin</strong> for in-app checks — no separate checkboxes.
        </p>
        <div className="max-h-40 overflow-y-auto rounded-lg border border-amber-200/80 dark:border-amber-900/50 bg-white/80 dark:bg-gray-900/50 px-2 py-1.5 space-y-1">
          {DEV_IMPERSONATION_ROLE_CHOICES.map((r) => (
            <label key={r.id} className="flex items-center gap-2 text-xs text-amber-950 dark:text-amber-100 cursor-pointer">
              <input
                type="checkbox"
                checked={selected.has(r.id)}
                onChange={() => toggleRole(r.id)}
                className="rounded border-amber-400 text-amber-600 focus:ring-amber-500"
              />
              {r.label}
            </label>
          ))}
        </div>

        {realPlatformDev ? (
          <label className="mt-2 flex items-start gap-2 text-[11px] text-amber-900 dark:text-amber-200 cursor-pointer">
            <input
              type="checkbox"
              checked={browseAllOrgs}
              onChange={(e) => setBrowseAllOrgs(e.target.checked)}
              className="mt-0.5 rounded border-amber-400 text-amber-600"
            />
            <span>
              Allow workspace org = <strong>any</strong> org (your real login must stay{" "}
              <span className="font-mono">platform_dev</span> — uses <span className="font-mono">platform_list_orgs</span>
              ).
            </span>
          </label>
        ) : null}

        <div className="mt-2 space-y-1">
          <label className="text-[11px] font-medium text-amber-950 dark:text-amber-100">
            Contacts trust-level override (testing)
          </label>
          <select
            className="w-full text-xs rounded-lg border border-amber-200 dark:border-amber-900 bg-white dark:bg-gray-900 px-2 py-1.5"
            value={trustLevelOverride}
            onChange={(e) => setTrustLevelOverride(e.target.value as DevImpersonationTrustOverride)}
          >
            <option value="auto">Auto (derive from simulated roles)</option>
            <option value="1">Force Level 1</option>
            <option value="2">Force Level 2</option>
            <option value="3">Force Level 3</option>
          </select>
          <p className="text-[10px] text-amber-800/80 dark:text-amber-300/80">
            Applies to Training - Contacts path-search testing. Requires impersonation enabled.
          </p>

          <label className="text-[11px] font-medium text-amber-950 dark:text-amber-100">Workspace organization</label>
          <select
            className="w-full text-xs rounded-lg border border-amber-200 dark:border-amber-900 bg-white dark:bg-gray-900 px-2 py-1.5"
            value={workspaceOrganizationId ?? ""}
            onChange={(e) => {
              const v = e.target.value.trim();
              setWorkspaceOrganizationId(v || null);
            }}
          >
            <option value="">— Profile default —</option>
            {orgOptions.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
          <p className="text-[10px] text-amber-800/80 dark:text-amber-300/80">
            One org at a time. “Browse any org” plus real <span className="font-mono">platform_dev</span> unlocks the full
            list; simulated platform without RPC access can paste a UUID below.
          </p>
          {enabled &&
          auth.hasRole("platform_admin", "platform_dev", "platform_trainer") &&
          platformOrgs.length === 0 ? (
            <div className="space-y-1">
              <label className="text-[10px] text-amber-900 dark:text-amber-200">Org UUID (optional)</label>
              <input
                className="w-full font-mono text-[10px] rounded border border-amber-200 dark:border-amber-800 bg-white dark:bg-gray-900 px-2 py-1"
                placeholder="00000000-0000-0000-0000-000000000000"
                onBlur={(e) => {
                  const v = e.target.value.trim();
                  if (isUuidLike(v)) setWorkspaceOrganizationId(v);
                }}
              />
            </div>
          ) : null}
        </div>

      </div>

      <DevImpersonationViewportAxis
        pending={pendingViewport}
        setPending={setPendingViewport}
        viewportActive={viewportActive}
        isDesktopBrowser={viewportDesktopBrowser}
      />

      <div className="flex flex-wrap gap-2 mt-1">
        <button
          type="button"
          {...{ [FIREFLY_TARGET_ATTR]: "dev-impersonation-apply" }}
          onClick={applyAll}
          disabled={!canApply}
          className="text-xs px-3 py-1.5 rounded-lg bg-amber-700 text-white hover:bg-amber-800 disabled:opacity-40"
        >
          Apply impersonation
        </button>
        <button
          type="button"
          onClick={clearAll}
          className="text-xs px-3 py-1.5 rounded-lg border border-amber-300 dark:border-amber-700 text-amber-900 dark:text-amber-200"
        >
          Clear all
        </button>
      </div>

      {/* Help text reminding the dev what Apply will do across axes. */}
      <p className="text-[10px] text-amber-800/80 dark:text-amber-300/80 leading-snug">
        Apply commits the impersonation roles AND the mobile viewport axis in one shot. Either axis alone is
        enough to enable the button.
      </p>
    </section>
  );
}
